import React from 'react';
import {
  Button,
  css,
  Icon,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SUB_PRODUCT_AREA,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';
import { useIntl } from '@leagueplatform/locales';
import { useLiveChatCta } from '@leagueplatform/live-chat';
import {
  getLiveChatFloatingButtonAnalytics,
  type LiveChatFloatingButtonAnalyticsContext,
} from 'common/constants';
import LiveChatStarsIndicatorIcon from '../../assets/live-chat-button-stars-indicator.svg';
import { UnreadMessageIndicator } from './live-chat-unread-message-indicator';
import LiveChatAISparklesIcon from '../../assets/AI-Sparkles.svg';

export type LiveChatCTAProps = {
  /** Which surface’s ticket taxonomy (screen, event id, `content` / `node`) applies */
  liveChatAnalyticsContext?: LiveChatFloatingButtonAnalyticsContext;
};

export function LiveChatCTA({
  liveChatAnalyticsContext = 'homepage',
}: LiveChatCTAProps = {}) {
  const { formatMessage } = useIntl();
  const { openChat, unreadMessageCount, liveChatButtonRef } = useLiveChatCta();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));

  const handleClick = () => {
    openChat();
    const analytics = getLiveChatFloatingButtonAnalytics(
      liveChatAnalyticsContext,
    );
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.GET_CARE,
      sub_product_area: SUB_PRODUCT_AREA.LIVE_CHAT,
      screen_name: analytics.screenName,
      detail: analytics.detail,
      event_id: analytics.eventId,
      content: analytics.content,
      node: analytics.node,
    });
  };

  const sharedButtonProps = {
    onClick: handleClick,
    ref: liveChatButtonRef,
    'aria-live': 'polite' as const,
    css: {
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
      ...css,
    },
  };

  return (
    <UnreadMessageIndicator
      unreadMessageCount={unreadMessageCount}
      css={{
        position: 'fixed',
        bottom: isMobile ? 16 : 32,
        right: isMobile ? 16 : 32,
        zIndex: '$sticky',
      }}
    >
      {isMobile ? (
        <Button
          {...sharedButtonProps}
          css={{
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            width: 48,
            height: 48,
            paddingTop: 18,
            paddingLeft: 10,
            borderRadius: '50%',
          }}
        >
          <Icon icon={LiveChatStarsIndicatorIcon} size={24} />
        </Button>
      ) : (
        <Button
          {...sharedButtonProps}
          css={{
            width: 205,
            height: 48,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '$threeQuarters $two',
            paddingRight: '$oneAndHalf',
          }}
        >
          {formatMessage({ id: 'LIVE_CHAT' })}
          <Icon
            icon={LiveChatAISparklesIcon}
            size={16}
            css={{ marginLeft: '$half' }}
          />
        </Button>
      )}
    </UnreadMessageIndicator>
  );
}
